import { useId, useState, type MouseEvent, type ReactNode } from 'react';
import { Menu, MenuItem, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RwIconButton from './RwIconButton';
import type { RwButtonSize, RwButtonTier } from './RwButton';
import { DS_MENU_PAPER_SX } from './internal/dsMenuPaperSx';

/**
 * The one divider above the destructive block. Canonical `.rw-menu-item` draws it as a plain CSS
 * `border-top`, but MUI's `ButtonBase` root resets `border: 0` in an emotion class that is injected
 * after `rw-ds.css`, so the DS rule never paints on a MUI `MenuItem`. `sx` lands in that same emotion
 * class, after the reset, so the divider is declared here too.
 */
const DIVIDER_ROW_SX = { borderTop: 'var(--border-width-xs) solid var(--border-neutral-subtle)' } as const;

export interface RwOverflowMenuItem {
  key: string;
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /**
   * Marks the row as a DESTRUCTIVE command.
   *
   * The test is the WORD, not the database (design ruling 2026-08-04): anything a reader would read
   * as Delete or Remove is destructive. This reverses an earlier, narrower rule that reserved the
   * register for commands which really destroy a record and left an unlink ("Remove from lead") as
   * a normal command — the two registers sitting side by side on one page was the thing that didn't
   * work.
   *
   * Renders `--text-critical` via `.rw-menu-item--destructive` (a CRM-local prototype of a DS gap —
   * canonical `rw-ds.css` §Menu item — destructive row).
   *
   * A destructive item must be LAST in `items`; that is where the one divider is drawn.
   */
  destructive?: boolean;
  /**
   * Explains why the item is disabled. Ignored when `disabled` is falsy. MUI's disabled elements
   * swallow pointer events, so the row is wrapped in a `<span>` only when this is set — an
   * unconditional wrapper would add an extra DOM layer to every menu item for no benefit.
   */
  tooltip?: string;
  /** Per-row test id — optional, since not every caller needs to target an individual row. */
  'data-testid'?: string;
}

/**
 * RwOverflowMenu — the trailing-action overflow, wired to the DS `.rw-menu` / `.rw-menu-item`
 * anatomy (rw-ds.css §Menu) via a MUI `Menu` for portal + collision-aware positioning. The table
 * toolbar collapses its trailing actions into this when space is constrained (rw-ds-tables §10).
 *
 * THE GLYPH IS ALWAYS ⋮ (design ruling 2026-08-04), on every surface — table row, feed row, card
 * header, detail header, toolbar. A horizontal ⋯ variant was tried and reversed: the glyph only
 * reads as a control when something nearby establishes it, and a single mark for a single meaning
 * beats a rule about axis alignment. There is therefore no `orientation` prop and no `MoreHorizIcon`
 * import — both were deleted with that change rather than left as an unused escape hatch. If a
 * genuine ⋯ case ever appears, it comes back as a deliberate decision, not by default.
 */
export interface RwOverflowMenuProps {
  items: RwOverflowMenuItem[];
  /**
   * Trigger footprint. `md` (40) is the toolbar default; `sm` (32) is for a list/feed row, whose
   * text line is far shorter than a 40px control — see the action-item rows.
   */
  size?: RwButtonSize;
  /** Trigger tier. Defaults to `RwIconButton`'s own default; a card overlay passes `ghost`. */
  tier?: RwButtonTier;
  'aria-label'?: string;
  'data-testid'?: string;
}

/**
 * Divider policy (design ruling 2026-08-04) — the divider inside an overflow has to MEAN something,
 * so it only ever separates the destructive block from the benign one:
 *  · 1 item                    → no divider
 *  · all-benign items          → no divider
 *  · mixed                     → exactly one divider, above the destructive item (always last)
 *
 * Canonical `.rw-menu-item` draws a `border-top` on every row but the first, so the implementation
 * is subtractive: every row gets `--no-divider` EXCEPT the first destructive row when something
 * precedes it. See canonical `rw-ds.css` §Menu item — destructive row.
 */
function menuItemClassName(item: RwOverflowMenuItem, index: number, dividerIndex: number): string {
  const drawsDivider = index === dividerIndex && index > 0;
  return ['rw-menu-item', drawsDivider ? null : 'rw-menu-item--no-divider', item.destructive ? 'rw-menu-item--destructive' : null]
    .filter(Boolean)
    .join(' ');
}

export default function RwOverflowMenu({
  items,
  size = 'md',
  tier,
  'aria-label': ariaLabel = 'More actions',
  'data-testid': testId
}: RwOverflowMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const triggerId = useId();
  const open = Boolean(anchorEl);

  const handleOpen = (e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const close = () => setAnchorEl(null);
  // The one divider sits above the FIRST destructive row; a second destructive row would not add
  // another (it belongs to the same block). -1 when the menu is all-benign → no divider anywhere.
  const dividerIndex = items.findIndex((item) => item.destructive === true);

  if (items.length === 0) return null;

  return (
    <>
      {/* THE GLYPH IS INTERACTIVE-COLOURED (design ruling 2026-08-04) — but NOT by anything this
          file does. The treatment hangs off the tier plus the icon-only shape in a shared CSS
          layer, so it applies to every ⋮ including ones hand-rolled without this component. There
          is no opt-in class to remember here — a class you have to remember is a convention, not a
          rule, and it had already been missed once. */}
      <RwIconButton
        id={triggerId}
        size={size}
        tier={tier}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-controls={open ? menuId : undefined}
        aria-expanded={open}
        data-testid={testId}
        onClick={handleOpen}
      >
        <MoreVertIcon sx={{ fontSize: 'var(--size-20)' }} />
      </RwIconButton>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        elevation={0}
        // NOT `className="rw-menu"`: canonical `.rw-menu` is written for the `.rw-dropdown` wrapper
        // case and carries `position:absolute; left:0; width:100%`. This paper is portalled to
        // <body>, so its containing block is the VIEWPORT and `width:100%` renders a 100vw-wide menu.
        // `DS_MENU_PAPER_SX` is the same canonical styling minus the positioning, hugging its content
        // and capped. The ROW class is safe — `.rw-menu-item` has no positioning in it.
        slotProps={{
          paper: { sx: DS_MENU_PAPER_SX },
          // Rows self-pad (canonical `.rw-menu` has no panel padding); MUI's List adds 8px top+bottom.
          // The menu is named after the control that opened it (`role="menu"` needs an accessible name).
          list: { sx: { py: 0 }, 'aria-labelledby': triggerId }
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {items.map((item, index) => {
          const menuItem = (
            <MenuItem
              key={item.key}
              className={menuItemClassName(item, index, dividerIndex)}
              sx={index === dividerIndex && index > 0 ? DIVIDER_ROW_SX : undefined}
              disabled={item.disabled}
              data-testid={item['data-testid']}
              onClick={() => {
                item.onClick();
                close();
              }}
            >
              {item.icon ? <span className="rw-menu-item__leading">{item.icon}</span> : null}
              <span className="rw-menu-item__label">{item.label}</span>
            </MenuItem>
          );

          if (!item.disabled || !item.tooltip) return menuItem;

          return (
            <Tooltip key={item.key} title={item.tooltip}>
              <span>{menuItem}</span>
            </Tooltip>
          );
        })}
      </Menu>
    </>
  );
}
